import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { FaMinus, FaSearch } from 'react-icons/fa'
import { FiDownload } from 'react-icons/fi'
import { getInvoice } from '../api/userApi'
import { handleDate } from '../handleDate'
import Pagination from './Pagination'

const Order = () => {
  document.title = 'Order | Dealstreetjournal'

  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['invoice', page],
    queryFn: () => getInvoice(page),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const totalPages = Math.ceil((data?.totalCount || 0) / 10)

  const handleMonth = (dat) => {
    const date = new Date(dat)
    const month = date
      .toLocaleDateString('default', {
        month: 'long',
      })
      .toLowerCase()

    return month
  }

  const filteredInvoice = useMemo(() => {
    if (!searchTerm) return data?.invoices || []

    const term = searchTerm.toLowerCase()

    return (
      data?.invoices?.filter((invoice) => {
        const safe = (val) => (val ? val.toString().toLowerCase() : '')

        return (
          safe(invoice.invoice).includes(term) ||
          safe(handleMonth(invoice.date)).includes(term) ||
          safe(invoice.orderId).includes(term) ||
          safe(invoice.amount).includes(term) ||
          safe(invoice.mailStatus).includes(term) ||
          safe(invoice.email).includes(term) ||
          safe(invoice.name).includes(term) ||
          safe(invoice.phone).includes(term) ||
          safe(invoice.state).includes(term) ||
          safe(invoice.country).includes(term)
        )
      }) || []
    )
  }, [searchTerm, data])

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <img
          src="../images/spinner.png"
          alt="Loading"
          className="w-12 h-12 animate-spin mb-2 mix-blend-multiply"
        />
      </div>
    )
  }

  if (isError) {
    return <span className="text-red-500">Error: {error.message}</span>
  }

  return (
    <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_0_20px_rgba(0,0,0,0.08)] text-gray-800">
      <h2 className="text-3xl font-bold text-center mb-10 text-[#ff7010]">
        Product Delivery
      </h2>

      {/* Search Section */}
      <div className="w-full mx-auto mb-8">
        <div className="relative max-w-md mx-auto">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by Date, OrderId, Email, State..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:ring-2 focus:ring-[#ff7010] focus:border-transparent outline-none transition-all"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#ff7010] text-white text-center">
              {[
                'Date',
                'Order Id',
                'Amount',
                'Details',
                'Mail Status',
                'Email',
                'Name / Phone',
                'State',
                'Country',
                'Invoice',
              ].map((head, idx) => (
                <th
                  key={idx}
                  className="px-4 py-3 font-semibold border-r border-[#ffa45a] last:border-r-0"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredInvoice?.length ? (
              filteredInvoice.map((row, i) => (
                <tr
                  key={i}
                  className={`transition-colors duration-150 ${
                    i % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  } hover:bg-orange-50`}
                >
                  <td className="px-4 py-3 text-center border-t border-gray-200">
                    {handleDate(row.date)}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 font-semibold">
                    {row.orderId}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 font-semibold">
                    ₹{row.amount}
                  </td>

                  <td className="px-4 py-3 border-t whitespace-nowrap border-gray-200">
                    <ul className="space-y-1 text-left ">
                      {row.products.map((d, j) => (
                        <li
                          key={j}
                          className="flex flex-wrap items-center gap-2"
                        >
                          <span className="text-gray-600 text-sm">
                            {handleDate(d.date) === 'Invalid Date'
                              ? d.date
                              : handleDate(d.date)}
                          </span>
                          <FaMinus className="text-gray-400 text-xs" />
                          <span className="font-semibold text-gray-800">
                            {d.productName}
                          </span>
                          <span className="text-gray-700">({d.insight})</span>
                          <span className="text-gray-800 font-medium">
                            ₹{d.price}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200">
                    <span
                      className={`px-3 py-1 rounded-full whitespace-nowrap text-sm font-semibold ${
                        row.mailStatus
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {row.mailStatus ? 'Sent' : 'Not Sent'}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 text-gray-700">
                    {row.email}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 text-gray-700">
                    {row.name} / {row.phone}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 text-gray-700">
                    {row.state}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200 text-gray-700">
                    {row.country}
                  </td>

                  <td className="px-4 py-3 text-center border-t border-gray-200">
                    {row.invoice}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="10"
                  className="text-center py-6 text-gray-500 italic"
                >
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex justify-center">
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  )
}

export default Order
